import {
  AMPLITUDE_API_KEY,
  amplitudeTrack,
  loadAmplitude,
} from "@/lib/amplitude";
import { appVersion } from "@/lib/appVersion";
import { loadScriptOnce, runWhenIdle } from "@/lib/idle";

type EventParams = Record<string, string | number | boolean | undefined>;
type GtagCommand = "js" | "config" | "event" | "set";
type EngagementEndReason = "navigate" | "hidden" | "pagehide" | "unmount";

type Gtag = (
  command: GtagCommand,
  target: string | Date,
  params?: EventParams,
) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Gtag;
  }
}

const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || "G-R9T6QJHL5X";

let initialized = false;

function routeNameForPath(pathname: string): string {
  if (pathname === "/") return "raceweekend";
  if (pathname === "/telemetry") return "telemetry";
  if (pathname === "/standings") return "standings";
  if (pathname === "/settings") return "settings";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/terms") return "terms";
  return "unknown";
}

function parseNumericParam(
  searchParams: URLSearchParams,
  key: string,
): number | undefined {
  const raw = searchParams.get(key);
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isLocalhostHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

function shouldTrackAnalytics(): boolean {
  return (
    (GA_MEASUREMENT_ID.length > 0 || AMPLITUDE_API_KEY.length > 0) &&
    typeof window !== "undefined" &&
    import.meta.env.PROD &&
    !isLocalhostHost(window.location.hostname)
  );
}

/** Sends one event to every configured provider (GA4 + Amplitude). */
function emitEvent(eventName: string, params: EventParams): void {
  if (GA_MEASUREMENT_ID) window.gtag?.("event", eventName, params);
  amplitudeTrack(eventName, params);
}

function screenClassForWidth(width: number): "mobile" | "tablet" | "desktop" {
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function referrerHost(): string | undefined {
  if (typeof document === "undefined" || !document.referrer) return undefined;
  try {
    return new URL(document.referrer).host;
  } catch {
    return document.referrer;
  }
}

function buildCommonEventParams(): EventParams {
  if (typeof window === "undefined") {
    return {
      ...buildAppVersionParams(),
    };
  }

  const searchParams = new URLSearchParams(window.location.search);

  return {
    ...buildAppVersionParams(),
    language: globalThis.navigator?.language,
    page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    route_name: routeNameForPath(window.location.pathname),
    season_year: parseNumericParam(searchParams, "year"),
    meeting_key: parseNumericParam(searchParams, "meeting"),
    session_key: parseNumericParam(searchParams, "session"),
    screen_class: screenClassForWidth(window.innerWidth),
    selected_view: searchParams.get("view") ?? undefined,
    viewport_height: window.innerHeight,
    viewport_width: window.innerWidth,
  };
}

function buildAppVersionParams(): EventParams {
  return appVersion ? { app_version: appVersion } : {};
}

export function initializeAnalytics(): void {
  if (initialized || !shouldTrackAnalytics() || typeof window === "undefined")
    return;
  initialized = true;
  loadAmplitude(appVersion || undefined);
  if (GA_MEASUREMENT_ID) {
    // gtag.js is ~180 KB of third-party JS. The inline stub in index.html
    // queues commands in dataLayer, so the real library can load once idle.
    runWhenIdle(() => {
      loadScriptOnce(
        "gtag-js",
        `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`,
      );
    });
    if (appVersion) {
      window.gtag?.("set", "user_properties", {
        app_version: appVersion,
      });
    }
    window.gtag?.("config", GA_MEASUREMENT_ID, {
      ...buildAppVersionParams(),
      send_page_view: false,
    });
  }
  emitEvent("app_session_started", {
    ...buildCommonEventParams(),
    landing_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    referrer_host: referrerHost(),
  });
}

export function trackPageView(path: string): void {
  if (!shouldTrackAnalytics()) return;
  emitEvent("page_view", {
    ...buildCommonEventParams(),
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
  });
}

export function trackEvent(eventName: string, params: EventParams = {}): void {
  if (!shouldTrackAnalytics()) return;
  emitEvent(eventName, {
    ...buildCommonEventParams(),
    ...params,
  });
}

export function trackPageEngagement(
  path: string,
  durationMs: number,
  reason: EngagementEndReason,
): void {
  if (!shouldTrackAnalytics()) return;
  const engagementTimeMs = Math.round(durationMs);
  if (engagementTimeMs < 1_000) return;
  emitEvent("page_engagement", {
    ...buildCommonEventParams(),
    engagement_time_msec: engagementTimeMs,
    exit_reason: reason,
    page_path: path,
  });
}

export function analyticsEnabled(): boolean {
  return shouldTrackAnalytics();
}
